import { CommonModule } from "@angular/common";
import { Component, computed, inject, signal, ChangeDetectionStrategy } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { FormsModule } from "@angular/forms";
import { BrandingFilter, ProductEx } from "@front/m19-models";
import { AccountSelectionService, AsinService } from "@front/m19-services";
import {
  IBadgeComponent,
  IButtonDirective,
  ICheckboxComponent,
  IDropdownMenuComponent,
  IDropdownMenuItemDirective,
  IDropdownMenuTriggerDirective,
  IInputDirective,
  ITableBodyDirective,
  ITableDirective,
  ITableHeadDirective,
  ITableRowDirective,
  ITableThDirective,
  IToggleComponent,
  ITooltipDirective,
  ModalService,
  ToasterService,
  IPaginatorComponent,
  IPaginationConfig,
  IIconComponent,
  ConfirmPopupComponent,
  SortingOption,
  SortByConfig,
  ISortSelectComponent,
} from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import {
  ImportManagedCatalogModalComponent,
  ImportManagedCatalogModalData,
  ImportManagedCatalogMode,
} from "@m19-board/catalog/import-managed-catalog-modal.component";
import { UnmanagedCatalogPageService } from "@m19-board/services/unmanaged-catalog-page.service";
import {
  CompactFilterComponent,
  Filter,
  FilterCategory,
} from "@m19-board/shared/compact-filter/compact-filter.component";
import { AsinFilter } from "@m19-board/shared/filter/asin-filter.service";
import { of, switchMap, take } from "rxjs";
import { CatalogRowComponent } from "../catalog-row/catalog-row.component";
import { CATALOG_DEFAULT_PAGE_SIZE, CatalogManagementMode } from "../catalog.component";
import { CatalogToolbarComponent } from "./catalog-toolbar/catalog-toolbar.component";
import { CatalogProductEx } from "@m19-board/services/managed-catalog-page.service";

@Component({
  selector: "app-unmanaged-catalog-page",
  imports: [
    IButtonDirective,
    ITooltipDirective,
    IBadgeComponent,
    CommonModule,
    CatalogRowComponent,
    ICheckboxComponent,
    TranslocoDirective,
    ITableDirective,
    ITableBodyDirective,
    ITableHeadDirective,
    ITableRowDirective,
    ITableThDirective,
    FormsModule,
    IInputDirective,
    IToggleComponent,
    CatalogToolbarComponent,
    IDropdownMenuComponent,
    IDropdownMenuItemDirective,
    IDropdownMenuTriggerDirective,
    CompactFilterComponent,
    IPaginatorComponent,
    IIconComponent,
    ISortSelectComponent,
  ],
  templateUrl: "./unmanaged-catalog-page.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UnmanagedCatalogPageComponent {
  private readonly unmanagedCatalogPageService = inject(UnmanagedCatalogPageService);
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly toastrService = inject(ToasterService);
  private readonly translocoService = inject(TranslocoService);
  private readonly asinService = inject(AsinService);
  private readonly modalService = inject(ModalService);

  readonly DEFAULT_PAGE_SIZE = CATALOG_DEFAULT_PAGE_SIZE;
  readonly CatalogManagementMode = CatalogManagementMode;
  readonly ImportManagedCatalogMode = ImportManagedCatalogMode;

  readonly unmanagedCatalog = this.unmanagedCatalogPageService.unmanagedCatalog;
  readonly selectedRows = this.unmanagedCatalogPageService.selectedRows;
  readonly isAllSelected = computed(() => {
    const selectedRowsLength = this.selectedRows().size;
    const filteredCatalogLength = this.filteredCatalog()?.length ?? 0;
    return selectedRowsLength === filteredCatalogLength && selectedRowsLength > 0;
  });
  readonly selectedRowsCount = computed(() => this.selectedRows().size);
  readonly isAutoCatalogManagement = this.unmanagedCatalogPageService.isAutoCatalogManagement;

  readonly filters = toSignal(this.unmanagedCatalogPageService.filters$);
  readonly isReadOnly = toSignal(this.accountSelectionService.readOnlyMode$);

  readonly sortByOptions: SortingOption<CatalogProductEx>[] = [
    {
      label: this.translocoService.translate("catalog-page.strategies_asc"),
      sortByField: "strategies",
      direction: "asc",
      compareFn: (a, b) => (a?.strategies?.length ?? 0) - (b?.strategies?.length ?? 0),
    },
    {
      label: this.translocoService.translate("catalog-page.strategies_desc"),
      sortByField: "strategies",
      direction: "desc",
      compareFn: (a, b) => (b?.strategies?.length ?? 0) - (a?.strategies?.length ?? 0),
    },
  ];
  readonly sortByConfig: SortByConfig<CatalogProductEx> = new SortByConfig(this.sortByOptions, this.sortByOptions[0]);

  textFilter = signal<string>("");
  pagination = signal<IPaginationConfig>({
    pageIndex: 0,
    pageSize: this.DEFAULT_PAGE_SIZE,
    length: 0,
  });
  selectedFilters = signal<Filter<AsinFilter>[]>([]);

  readonly filteredCatalog = computed(() => {
    const filters = this.selectedFilters();
    const sortBy = this.sortByConfig.selectedSortBy();
    const filteredCatalog = this.unmanagedCatalog()?.filter((product) => {
      const brandFilter = filters.find((filter) => filter.type === FilterCategory.BrandProducts);
      return this.passesTextFilter(product) && this.passesBrandFilter(product, brandFilter);
    });
    if (sortBy) {
      filteredCatalog?.sort((a, b) => sortBy.compareFn!(a, b));
    }
    return filteredCatalog;
  });

  readonly catalogPaginated = computed(() =>
    this.filteredCatalog()?.slice(
      this.pagination().pageIndex * this.pagination().pageSize,
      (this.pagination().pageIndex + 1) * this.pagination().pageSize,
    ),
  );

  onRowCheckboxClicked(asin: string) {
    this.selectedRows.update((selectedRows) => {
      if (selectedRows.has(asin)) {
        selectedRows.delete(asin);
      } else {
        selectedRows.add(asin);
      }
      return new Set([...selectedRows]);
    });
  }

  toggleSelectAllProducts() {
    if (this.isAllSelected()) {
      this.clearSelection();
    } else {
      this.selectedRows.set(new Set(this.filteredCatalog()?.map((product) => product.asin!) ?? []));
    }
  }

  clearSelection() {
    this.selectedRows.set(new Set<string>());
  }

  restoreManagingProducts() {
    let hasActiveStrategies = false;
    for (const asin of this.selectedRows()) {
      const product = this.unmanagedCatalog()?.find((p) => p.asin === asin);
      if (product?.strategies && product.strategies.length > 0) {
        hasActiveStrategies = true;
        break;
      }
    }

    (hasActiveStrategies
      ? this.modalService
          .openModal(ConfirmPopupComponent, {
            data: {
              message:
                this.selectedRows().size === 1
                  ? this.translocoService.translate(
                      "catalog-page.confirm_restore_managing_single_product_active_strategies",
                    )
                  : this.translocoService.translate(
                      "catalog-page.confirm_restore_managing_multiple_products_active_strategies",
                    ),
              color: "orange",
            },
          })
          .afterClosed()
      : of(true)
    )
      .pipe(
        switchMap((confirmed) => {
          if (confirmed) {
            return this.unmanagedCatalogPageService.restoreManagingProducts(Array.from(this.selectedRows()));
          }
          return of(null);
        }),
      )
      .subscribe({
        next: (result) => {
          if (result === null) {
            return;
          }
          this.toastrService.success(this.translocoService.translate("catalog-page.products_catalog_updated"));
          this.clearSelection();
          this.textFilter.set("");
        },
        error: (err) => {
          this.toastrService.error(
            err,
            this.translocoService.translate("catalog-page.failed_to_move_back_products_to_managed_catalog"),
          );
        },
      });
  }

  toggleCatalogManagementMode(mode: CatalogManagementMode) {
    this.accountSelectionService.singleAccountMarketplaceSelection$
      .pipe(
        take(1),
        switchMap((accountMarketplace) =>
          this.asinService.setCatalogManagementMode(accountMarketplace.accountId, accountMarketplace.marketplace, mode),
        ),
      )
      .subscribe({
        next: () => {
          this.toastrService.success(
            mode === CatalogManagementMode.AUTO
              ? this.translocoService.translate("catalog-page.catalog_management_mode_set_to_auto")
              : this.translocoService.translate("catalog-page.catalog_management_mode_set_to_manual"),
          );
        },
        error: (err) => {
          this.toastrService.error(
            err,
            this.translocoService.translate("catalog-page.failed_to_set_catalog_management_mode"),
          );
        },
      });
  }

  bulkImportUnmanagedProducts(mode: ImportManagedCatalogMode) {
    this.modalService.openModal<ImportManagedCatalogModalData, void>(ImportManagedCatalogModalComponent, {
      size: "md",
      data: {
        mode,
      },
    });
  }

  passesTextFilter(product: ProductEx) {
    return (
      product.asin?.toLowerCase().includes(this.textFilter().toLowerCase()) ||
      product.title?.toLowerCase().includes(this.textFilter().toLowerCase()) ||
      product.brand?.toLowerCase().includes(this.textFilter().toLowerCase())
    );
  }

  private passesBrandFilter(product: ProductEx, filter: Filter<AsinFilter> | undefined) {
    if (!filter || filter.values.length === 0) {
      return true;
    }
    if (filter.type === FilterCategory.BrandProducts) {
      const hasBrand = filter.values.find(
        (value) => product.brand?.toLocaleLowerCase() === (value as BrandingFilter).name.toLocaleLowerCase(),
      );
      if (!hasBrand) return false;
    }
    return true;
  }
}
